import React, { useState, useEffect, useCallback } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import {
  fetchDocumentBankItem,
  deleteDocumentBankItem,
  downloadDocumentBankFile,
  fetchDocumentBankAssignments,
  unassignDocumentFromCourse,
  assignDocumentToCourse,
} from "../../api/backend";
import useAssignableClasses from "../../hooks/useAssignableClasses";
import AssignClassRow from "../../components/assignClass/AssignClassRow";

const D = {
  ink: "var(--color-ink)", ink2: "rgba(31,22,51,0.75)", ink3: "rgba(31,22,51,0.6)", ink4: "rgba(31,22,51,0.5)",
  line: "var(--color-hairline-cloud)", line2: "var(--color-hairline-cloud)",
  surface: "var(--color-surface-canvas-light)", surface2: "var(--color-surface-press-light)", surface3: "var(--color-surface-press-stronger)",
  green: "var(--color-success)", green50: "var(--color-success-bg)", green100: "var(--color-success-bg)",
  amber: "var(--color-warn)", amber50: "var(--color-warn-bg)", amber100: "var(--color-warn-bg)",
  live: "#c23d68", live50: "color-mix(in srgb, var(--color-accent-pink) 10%, transparent)", live100: "color-mix(in srgb, var(--color-accent-pink) 22%, transparent)",
  danger: "var(--color-danger)", danger50: "var(--color-danger-bg)", danger100: "var(--color-danger-bg)",
  blue: "var(--color-primary)", blue50: "rgba(37,99,235,.10)",
  purple: "var(--color-accent-violet-deep)", purple50: "color-mix(in srgb, var(--color-accent-violet-deep) 10%, transparent)",
  shadowSm: "var(--shadow-card-sm)",
  shadowMd: "var(--shadow-card-md)",
};

const CAT_COLOR = {
  firefighter: { bg: D.live50, color: D.live },
  ems: { bg: D.blue50, color: D.blue },
  hazmat: { bg: D.amber50, color: D.amber },
  rescue: { bg: D.green50, color: D.green },
  officer: { bg: D.purple50, color: D.purple },
};
const FT_COLORS = {
  pdf:  { barBg: D.danger, badgeBg: D.danger50, badgeColor: D.danger },
  docx: { barBg: D.blue, badgeBg: D.blue50, badgeColor: D.blue },
  xlsx: { barBg: D.green, badgeBg: D.green50, badgeColor: D.green },
  pptx: { barBg: D.live, badgeBg: D.live50, badgeColor: D.live },
};

function parseTags(raw = "[]") {
  try { const v = JSON.parse(raw); return Array.isArray(v) ? v : []; } catch { return raw.split(",").map(s => s.trim()).filter(Boolean); }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return "—"; }
}

function fmtSize(bytes) {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function getFileType(item) {
  if (!item) return null;
  const name = (item.fileName || "").toLowerCase();
  const ct = (item.fileContentType || "").toLowerCase();
  if (name.endsWith(".pdf") || ct.includes("pdf")) return "pdf";
  if (name.endsWith(".docx") || name.endsWith(".doc") || ct.includes("word")) return "docx";
  if (name.endsWith(".xlsx") || name.endsWith(".xls") || ct.includes("excel") || ct.includes("spreadsheet")) return "xlsx";
  if (name.endsWith(".pptx") || name.endsWith(".ppt") || ct.includes("presentation")) return "pptx";
  if (item.contentType === "LINK") return "link";
  return null;
}

function getIconStyle(tags = [], tPrimary = "var(--color-primary)") {
  const t = tags.map(s => s.toLowerCase()).join(" ");
  for (const [k, v] of Object.entries(CAT_COLOR)) if (t.includes(k)) return v;
  return { bg: alpha(tPrimary, 0.06), color: tPrimary };
}

function Ico({ size = 15, sw = 2, children, style }) {
  return <Box component="svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={style}>{children}</Box>;
}

function Btn({ children, primary, secondary, ghost, danger, sm, block, onClick, disabled, icon }) {
  const { palette } = useTheme();
  const tPrimary = palette.primary.main;
  const tPrimaryDk = palette.primary.dark;
  const h = sm ? 36 : 40;
  const base = { height: h, px: sm ? "13px" : "17px", borderRadius: sm ? "9px" : "11px", border: 0, fontFamily: "inherit", fontSize: sm ? "13px" : "13.5px", fontWeight: 700, cursor: disabled ? "default" : "pointer", display: "inline-flex", alignItems: "center", gap: "8px", transition: "all .13s", whiteSpace: "nowrap", width: block ? "100%" : undefined, justifyContent: block ? "center" : undefined };
  const v = primary ? { bgcolor: disabled ? D.surface3 : tPrimary, color: disabled ? D.ink3 : "var(--color-surface-canvas-light)", boxShadow: `0 4px 12px -4px ${alpha(tPrimary, 0.45)}`, "&:hover:not(:disabled)": { bgcolor: tPrimaryDk } }
    : danger ? { bgcolor: D.danger50, color: D.danger, border: `1.5px solid ${D.danger100}`, "&:hover": { bgcolor: D.danger100 } }
    : secondary ? { bgcolor: "var(--color-surface-canvas-light)", color: D.ink2, border: `1.5px solid ${D.line}`, "&:hover": { bgcolor: D.surface3, borderColor: "var(--color-hairline-cool)" } }
    : { bgcolor: "transparent", color: D.ink3, border: `1.5px solid ${D.line}`, "&:hover": { bgcolor: D.surface3 } };
  return <Box component="button" onClick={onClick} disabled={disabled} sx={{ ...base, ...v }}>{icon}{children}</Box>;
}

function IconBtn({ onClick, dangerHover, title, children }) {
  return (
    <Box component="button" title={title} onClick={onClick} sx={{ width: 36, height: 36, borderRadius: "9px", border: `1.5px solid ${D.line}`, bgcolor: "var(--color-surface-canvas-light)", display: "grid", placeItems: "center", cursor: "pointer", color: D.ink3, transition: "all .12s", flexShrink: 0, "&:hover": dangerHover ? { bgcolor: D.danger50, color: D.danger, borderColor: D.danger100 } : { bgcolor: D.surface3, color: D.ink, borderColor: "var(--color-hairline-cool)" } }}>
      {children}
    </Box>
  );
}

function Panel({ children, highlight }) {
  const { palette } = useTheme();
  const tPrimary = palette.primary.main;
  return <Box sx={{ bgcolor: "var(--color-surface-canvas-light)", border: highlight ? `1.5px solid ${alpha(tPrimary, 0.2)}` : `1px solid ${D.line}`, borderRadius: "16px", boxShadow: highlight ? `0 8px 26px -14px ${alpha(tPrimary, 0.34)}` : D.shadowSm, overflow: "hidden" }}>{children}</Box>;
}

function PanelHead({ icon, iconBg, iconColor, title, badge, action }) {
  const { palette } = useTheme();
  const tPrimary = palette.primary.main;
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "16px 20px 14px", borderBottom: `1px solid ${D.line2}` }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "11px" }}>
        <Box sx={{ width: 30, height: 30, borderRadius: "8px", display: "grid", placeItems: "center", bgcolor: iconBg || alpha(tPrimary, 0.06), color: iconColor || tPrimary, flexShrink: 0 }}>{icon}</Box>
        <Typography sx={{ fontSize: "15px", fontWeight: 600, letterSpacing: "-.01em", color: D.ink, m: 0, fontFamily: "var(--font-display)" }}>{title}</Typography>
        {badge != null && <Box sx={{ fontSize: "11px", fontWeight: 700, px: "8px", py: "2px", borderRadius: "7px", bgcolor: D.surface3, color: D.ink3 }}>{badge}</Box>}
      </Box>
      {action}
    </Box>
  );
}

function Tag({ label }) {
  const t = label.toLowerCase();
  const cat = Object.entries(CAT_COLOR).find(([k]) => t.includes(k));
  if (cat) return <Box component="span" sx={{ fontSize: "11.5px", fontWeight: 700, px: "10px", py: "3px", borderRadius: "999px", bgcolor: cat[1].bg, color: cat[1].color, border: `1px solid color-mix(in srgb, ${cat[1].color} 30%, transparent)` }}>{label}</Box>;
  return <Box component="span" sx={{ fontSize: "11.5px", fontWeight: 700, px: "10px", py: "3px", borderRadius: "999px", bgcolor: D.surface3, color: D.ink2, border: `1px solid ${D.line}` }}>{label}</Box>;
}

function StatTile({ icon, iconBg, iconColor, value, label }) {
  return (
    <Box sx={{ bgcolor: "var(--color-surface-canvas-light)", border: `1px solid ${D.line}`, borderRadius: "14px", p: "15px 17px", boxShadow: D.shadowSm, display: "flex", alignItems: "center", gap: "13px" }}>
      <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: iconBg, color: iconColor, display: "grid", placeItems: "center", flexShrink: 0 }}>{icon}</Box>
      <Box>
        <Box sx={{ fontSize: "24px", fontWeight: 800, letterSpacing: "-.04em", lineHeight: 1, color: D.ink }}>{value}</Box>
        <Box sx={{ fontSize: "12px", fontWeight: 600, color: D.ink3, mt: "3px" }}>{label}</Box>
      </Box>
    </Box>
  );
}

function MetaRow({ label, value }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", py: "11px", borderBottom: `1px solid ${D.line2}`, "&:last-child": { borderBottom: 0, pb: 0 }, "&:first-of-type": { pt: 0 } }}>
      <Box sx={{ fontSize: "12.5px", fontWeight: 600, color: D.ink3 }}>{label}</Box>
      <Box sx={{ fontSize: "13px", fontWeight: 700, color: D.ink, textAlign: "right" }}>{value}</Box>
    </Box>
  );
}

function AssignModal({ open, onClose, docId, alreadyAssigned, onAssigned }) {
  const { palette } = useTheme();
  const tPrimary = palette.primary.main;
  const { courses, loading, error: loadError } = useAssignableClasses(open);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [assigning, setAssigning] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { if (open) { setSearch(""); setSelected(new Set()); } }, [open]);

  const assignedIds = new Set((alreadyAssigned || []).map(a => a.courseId));
  const filtered = courses.filter(c => !search || c.title?.toLowerCase().includes(search.toLowerCase()) || c.courseNumber?.toLowerCase().includes(search.toLowerCase()));
  const toggle = id => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const handleConfirm = async () => {
    if (!selected.size) return;
    setAssigning(true); setErr(null);
    try {
      await Promise.all([...selected].map(courseId => assignDocumentToCourse(courseId, { documentId: docId })));
      onAssigned(); onClose();
    } catch (e) { setErr(e?.response?.data?.message || "Failed to assign."); }
    finally { setAssigning(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", gap: "13px", p: "22px 24px 18px", borderBottom: `1px solid ${D.line2}` }}>
        <Box sx={{ width: 40, height: 40, borderRadius: "11px", bgcolor: tPrimary, color: "var(--color-surface-canvas-light)", display: "grid", placeItems: "center", flexShrink: 0 }}>
          <Ico size={20}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></Ico>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ fontSize: "18px", fontWeight: 800, color: D.ink }}>Assign Document</Box>
          <Box sx={{ fontSize: "13px", color: D.ink3, mt: "3px" }}>Select classes to add this document to.</Box>
        </Box>
        <Box component="button" onClick={onClose} sx={{ width: 32, height: 32, borderRadius: "8px", border: 0, bgcolor: D.surface3, color: D.ink3, cursor: "pointer", display: "grid", placeItems: "center", "&:hover": { bgcolor: D.danger50, color: D.danger } }}>
          <Ico size={16}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></Ico>
        </Box>
      </DialogTitle>
      <Box sx={{ p: "14px 24px", borderBottom: `1px solid ${D.line2}` }}>
        <Box sx={{ position: "relative" }}>
          <Box sx={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)", color: D.ink4, display: "flex" }}><Ico size={15}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></Ico></Box>
          <Box component="input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search classes…" sx={{ height: 42, pl: "38px", pr: "14px", bgcolor: D.surface2, border: `1.5px solid ${D.line}`, borderRadius: "11px", fontFamily: "inherit", fontSize: "14px", color: D.ink, outline: "none", width: "100%", "&:focus": { borderColor: tPrimary, boxShadow: `0 0 0 3px ${alpha(tPrimary, 0.08)}`, bgcolor: "var(--color-surface-canvas-light)" } }} />
        </Box>
      </Box>
      <DialogContent sx={{ p: "10px 16px", maxHeight: 380, overflowY: "auto" }}>
        {(err || loadError) && <Alert severity="error" sx={{ mb: 1, borderRadius: "8px" }}>{err || loadError}</Alert>}
        {!loading && !loadError && filtered.length === 0 && <Box sx={{ textAlign: "center", color: D.ink3, fontSize: "13px", py: 4 }}>{search ? "No classes match your search." : "No classes available to assign to."}</Box>}
        {loading ? <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={24} sx={{ color: tPrimary }} /></Box>
          : filtered.map(c => <AssignClassRow key={c.id} course={c} assigned={assignedIds.has(c.id)} selected={selected.has(c.id)} onToggle={toggle} />)}
      </DialogContent>
      <DialogActions sx={{ p: "16px 24px", borderTop: `1px solid ${D.line2}`, bgcolor: D.surface2, display: "flex", justifyContent: "space-between" }}>
        <Box sx={{ fontSize: "12.5px", fontWeight: 600, color: D.ink3 }}>{selected.size} class{selected.size !== 1 ? "es" : ""} selected</Box>
        <Box sx={{ display: "flex", gap: "9px" }}>
          <Btn ghost sm onClick={onClose}>Cancel</Btn>
          <Btn primary sm onClick={handleConfirm} disabled={!selected.size || assigning} icon={assigning ? <CircularProgress size={12} sx={{ color: "inherit" }} /> : <Ico size={13} sw={2.5}><polyline points="20 6 9 17 4 12"/></Ico>}>Assign Selected</Btn>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// ── Document thumbnail ────────────────────────────────────────────────────────
function DocThumbnail({ ft, fileName }) {
  const { palette } = useTheme();
  const tPrimary = palette.primary.main;
  const ftc = FT_COLORS[ft] || { barBg: tPrimary, badgeBg: alpha(tPrimary, 0.06), badgeColor: tPrimary };
  return (
    <Box sx={{ width: 150, height: 194, borderRadius: "11px", flexShrink: 0, bgcolor: "linear-gradient(160deg,var(--color-surface-canvas-light),var(--color-surface-press-stronger))", border: `1px solid ${D.line}`, boxShadow: D.shadowMd, position: "relative", overflow: "hidden", display: "flex", flexDirection: "column", background: "linear-gradient(160deg,var(--color-surface-canvas-light),var(--color-surface-press-stronger))" }}>
      <Box sx={{ height: 46, bgcolor: ftc.barBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Ico size={20} style={{ color: "var(--color-surface-canvas-light)", stroke: "var(--color-surface-canvas-light)" }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></Ico>
      </Box>
      <Box sx={{ p: "14px", display: "flex", flexDirection: "column", gap: "7px" }}>
        {[80, 100, 60, 100, 80, 100, 60, 80].map((w, i) => (
          <Box key={i} sx={{ height: "5px", borderRadius: "999px", bgcolor: D.line2, width: `${w}%` }} />
        ))}
      </Box>
      <Box sx={{ position: "absolute", bottom: "10px", left: "14px", fontSize: "9px", fontWeight: 700, color: D.ink4, fontFamily: "monospace", textTransform: "uppercase" }}>
        {ft || "FILE"}
      </Box>
    </Box>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────
export default function DocumentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { palette } = useTheme();
  const tPrimary = palette.primary.main;
  const tSecondary = palette.secondary.main;
  const tAccent = palette.warning.main;

  const [doc, setDoc] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [assignOpen, setAssignOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, a] = await Promise.all([
        fetchDocumentBankItem(id),
        fetchDocumentBankAssignments(id).catch(() => []),
      ]);
      setDoc(d);
      setAssignments(Array.isArray(a) ? a : []);
    } catch { setErr("Failed to load document."); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async () => {
    if (doc?.url) { window.open(doc.url, "_blank"); return; }
    setDownloading(true);
    try {
      const blob = await downloadDocumentBankFile(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = doc.fileName || doc.title;
      a.click(); URL.revokeObjectURL(url);
    } catch { setErr("Download failed."); }
    finally { setDownloading(false); }
  };

  const handleUnassign = async (a) => {
    try { await unassignDocumentFromCourse(id, a.courseId); setAssignments(prev => prev.filter(x => x.assignmentId !== a.assignmentId)); }
    catch { setErr("Failed to unassign."); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await deleteDocumentBankItem(id); navigate("/instructor/knowledge-bank"); }
    catch (e) { setErr(e?.response?.data?.message || "Cannot delete."); setDeleteConfirm(false); }
    finally { setDeleting(false); }
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", pt: 10 }}><CircularProgress sx={{ color: tPrimary }} /></Box>;
  if (err && !doc) return <Box sx={{ maxWidth: 600, mx: "auto", pt: 6 }}><Alert severity="error">{err}</Alert></Box>;
  if (!doc) return null;

  const tags = parseTags(doc.tags);
  const ft = getFileType(doc);
  const ftc = FT_COLORS[ft] || { barBg: tPrimary, badgeBg: alpha(tPrimary, 0.06), badgeColor: tPrimary };
  const { bg: iconBg, color: iconColor } = getIconStyle(tags, tPrimary);
  const sizeStr = fmtSize(doc.fileSize);

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2, borderRadius: "10px" }}>{err}</Alert>}

      <Box component="button" onClick={() => navigate("/instructor/knowledge-bank")}
        sx={{ display: "inline-flex", alignItems: "center", gap: "7px", fontSize: "13px", fontWeight: 600, color: D.ink3, border: 0, bgcolor: "transparent", cursor: "pointer", mb: "16px", p: 0, "&:hover": { color: tPrimary } }}>
        <Ico size={15} sw={2.2}><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></Ico>
        Back to Knowledge Bank
      </Box>

      {/* Hero */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "18px", mb: "20px", flexWrap: "wrap" }}>
        <Box sx={{ width: 56, height: 56, borderRadius: "15px", bgcolor: ft ? ftc.badgeBg : iconBg, color: ft ? ftc.badgeColor : iconColor, display: "grid", placeItems: "center", flexShrink: 0 }}>
          <Ico size={26}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></Ico>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "28px", fontWeight: 600, letterSpacing: "-.01em", lineHeight: 1.15, color: D.ink, m: 0, fontFamily: "var(--font-display)" }}>{doc.title}</Typography>
          {doc.description && <Box sx={{ fontSize: "14.5px", color: D.ink3, mt: "6px", maxWidth: 560 }}>{doc.description}</Box>}
          <Box sx={{ display: "flex", alignItems: "center", gap: "7px", flexWrap: "wrap", mt: "11px" }}>
            {ft && <Box component="span" sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: ".05em", px: "8px", py: "3px", borderRadius: "6px", fontFamily: "monospace", textTransform: "uppercase", bgcolor: ftc.badgeBg, color: ftc.badgeColor }}>{ft}</Box>}
            {tags.map(t => <Tag key={t} label={t} />)}
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0, flexWrap: "wrap" }}>
          <IconBtn title="Download" onClick={handleDownload}>
            {downloading ? <CircularProgress size={14} sx={{ color: D.ink3 }} /> : <Ico size={15}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></Ico>}
          </IconBtn>
          <Btn primary sm onClick={() => setAssignOpen(true)} icon={<Ico size={14}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></Ico>}>Assign to Class</Btn>
        </Box>
      </Box>

      {/* Stat strip */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: "12px", mb: "22px" }}>
        <StatTile icon={<Ico size={18}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/></Ico>} iconBg={alpha(tPrimary, 0.06)} iconColor={tPrimary} value={assignments.length} label="Assigned Classes" />
        <StatTile icon={<Ico size={18}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></Ico>} iconBg={alpha(tSecondary, 0.1)} iconColor={tSecondary} value="—" label="Downloads" />
        <StatTile icon={<Ico size={18}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></Ico>} iconBg={alpha(tAccent, 0.1)} iconColor={tAccent} value={sizeStr || "—"} label="File Size" />
        <StatTile icon={<Ico size={18}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></Ico>} iconBg={D.live50} iconColor={D.live} value={(ft || doc.contentType || "—").toUpperCase()} label="Type" />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 340px" }, gap: "20px", alignItems: "start" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 }}>

          {/* Document overview */}
          <Panel>
            <PanelHead icon={<Ico size={15}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></Ico>} title="Document"
              action={doc.url && <Btn secondary sm onClick={() => window.open(doc.url, "_blank")} icon={<Ico size={13}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></Ico>}>Open Link</Btn>} />
            <Box sx={{ p: "18px 20px" }}>
              <Box sx={{ display: "flex", gap: "18px", alignItems: "flex-start", flexWrap: "wrap" }}>
                {ft && ft !== "link" && <DocThumbnail ft={ft} fileName={doc.fileName} />}
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Box sx={{ fontSize: "16px", fontWeight: 700, color: D.ink }}>{doc.fileName || doc.title}</Box>
                  {doc.description && <Box sx={{ fontSize: "13px", color: D.ink3, mt: "3px", lineHeight: 1.55 }}>{doc.description}</Box>}
                  {doc.url && <Box sx={{ fontSize: "13px", color: tPrimary, mt: "6px", wordBreak: "break-all" }}>{doc.url}</Box>}

                  <Box sx={{ display: "flex", gap: "18px", flexWrap: "wrap", mt: "16px", pt: "14px", borderTop: `1px solid ${D.line2}` }}>
                    {ft && <Box><Box sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: D.ink4 }}>Type</Box><Box sx={{ fontSize: "15px", fontWeight: 800, color: D.ink, mt: "3px" }}>{ft.toUpperCase()}</Box></Box>}
                    {sizeStr && <Box><Box sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: D.ink4 }}>Size</Box><Box sx={{ fontSize: "15px", fontWeight: 800, color: D.ink, mt: "3px" }}>{sizeStr}</Box></Box>}
                    {doc.createdAt && <Box><Box sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: D.ink4 }}>Uploaded</Box><Box sx={{ fontSize: "15px", fontWeight: 800, color: D.ink, mt: "3px" }}>{fmtDate(doc.createdAt)}</Box></Box>}
                  </Box>

                  <Box sx={{ display: "flex", gap: "9px", mt: "18px" }}>
                    {(doc.fileData || doc.fileName || doc.url) && (
                      <Btn primary sm onClick={handleDownload} icon={<Ico size={13}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></Ico>}>
                        {doc.url ? "Open Link" : "Download"}
                      </Btn>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Panel>
        </Box>

        {/* Sidebar */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", position: { lg: "sticky" }, top: { lg: "76px" } }}>

          {/* Assigned Classes */}
          <Panel highlight>
            <Box sx={{ bgcolor: alpha(tPrimary, 0.06), borderBottom: `1px solid ${alpha(tPrimary, 0.12)}` }}>
              <PanelHead icon={<Ico size={15}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></Ico>} title="Assigned Classes" badge={assignments.length} />
            </Box>
            <Box sx={{ p: "10px 20px 14px" }}>
              {assignments.length === 0 ? (
                <Box sx={{ textAlign: "center", color: D.ink3, fontSize: "13px", py: "8px" }}>Not assigned to any classes yet.</Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {assignments.map(a => (
                    <Box key={a.assignmentId} sx={{ display: "flex", alignItems: "center", gap: "13px", py: "13px", borderBottom: `1px solid ${D.line2}`, "&:last-child": { borderBottom: 0, pb: "2px" }, "&:first-of-type": { pt: "2px" } }}>
                      <Box sx={{ width: 38, height: 38, borderRadius: "10px", bgcolor: D.live50, color: D.live, display: "grid", placeItems: "center", flexShrink: 0 }}>
                        <Ico size={18}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></Ico>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ fontSize: "14px", fontWeight: 700, color: D.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.courseTitle}</Box>
                        {a.courseNumber && <Box sx={{ fontSize: "12px", fontFamily: "monospace", fontWeight: 700, color: D.ink3 }}>{a.courseNumber}</Box>}
                      </Box>
                      <IconBtn dangerHover title="Unassign" onClick={() => handleUnassign(a)}>
                        <Ico size={13}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></Ico>
                      </IconBtn>
                    </Box>
                  ))}
                </Box>
              )}
              <Btn primary block onClick={() => setAssignOpen(true)} icon={<Ico size={14} sw={2.5}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></Ico>} style={{ marginTop: "14px" }}>Assign to Class</Btn>
            </Box>
          </Panel>

          {/* Details */}
          <Panel>
            <PanelHead icon={<Ico size={15}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></Ico>} iconBg={D.surface3} iconColor={D.ink3} title="Details" />
            <Box sx={{ p: "2px 20px 18px" }}>
              {ft && <MetaRow label="File type" value={`${ft.toUpperCase()} ${ft !== "link" ? "Document" : ""}`} />}
              {sizeStr && <MetaRow label="File size" value={sizeStr} />}
              {doc.contentType && <MetaRow label="Content type" value={doc.contentType} />}
              <MetaRow label="Uploaded" value={fmtDate(doc.createdAt)} />
              <MetaRow label="Last edited" value={fmtDate(doc.updatedAt)} />
            </Box>
          </Panel>

          {/* Danger zone */}
          <Panel>
            <Box sx={{ p: "18px 20px", display: "flex", flexDirection: "column", gap: "9px" }}>
              <Btn secondary block icon={<Ico size={14}><path d="M21 8v13H3V8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></Ico>}>Archive Document</Btn>
              <Btn danger block onClick={() => setDeleteConfirm(true)} icon={<Ico size={14}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></Ico>}>Delete Document</Btn>
            </Box>
          </Panel>
        </Box>
      </Box>

      <AssignModal open={assignOpen} onClose={() => setAssignOpen(false)} docId={id} alreadyAssigned={assignments} onAssigned={() => fetchDocumentBankAssignments(id).then(d => setAssignments(Array.isArray(d) ? d : []))} />

      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)} PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontSize: "17px", fontWeight: 800, color: D.ink }}>Delete Document?</DialogTitle>
        <DialogContent><Typography sx={{ fontSize: "13.5px", color: D.ink3 }}>This will permanently delete "{doc.title}". Cannot be undone.</Typography></DialogContent>
        <DialogActions sx={{ p: "12px 24px 18px", gap: "8px" }}>
          <Btn ghost sm onClick={() => setDeleteConfirm(false)}>Cancel</Btn>
          <Btn danger sm onClick={handleDelete} disabled={deleting} icon={deleting ? <CircularProgress size={12} sx={{ color: "inherit" }} /> : null}>{deleting ? "Deleting…" : "Delete"}</Btn>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
